import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Invite } from '../../models';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomValidators } from '../../../../../shared';
import { SignInRequest } from '../../../../../shared/models/auth';
import { AuthService } from '../../../../../shared/auth/services';
import { CommunicationStateComponent } from '../../../../../shared';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../app.reducers';
import { LoginAction } from '../../../../../shared/auth/redux/auth.actions';
import { SetPrivateLabelLogoAction } from 'app/public/redux/public.actions';
import { SubSink } from 'subsink';
import { AccountService } from '../../../../../shared';

@Component({
  templateUrl: './invite-details.component.html',
  standalone: false
})
export class InviteDetailsComponent implements OnInit, OnDestroy {

  @ViewChild(CommunicationStateComponent, { static: true }) public communicationState: CommunicationStateComponent;

  firstName: UntypedFormControl = new UntypedFormControl();
  lastName: UntypedFormControl = new UntypedFormControl();
  password: UntypedFormControl = new UntypedFormControl();
  confirmPassword: UntypedFormControl = new UntypedFormControl();
  invitationCode: string;
  email: string;
  userId: string;
  requireConfirmPassword = false;
  isSubmitting = false;
  feedbackMessages: any[] = [];
  inviteDetailsForm: UntypedFormGroup = new UntypedFormGroup({
    firstName: new UntypedFormControl(),
    lastName: new UntypedFormControl(),
  });
  showSigninLink = false;
  private subs = new SubSink();

  constructor(private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private authService: AuthService,
    private formBuilder: UntypedFormBuilder,
    private store: Store<AppState>) {
  }

  ngOnInit() {

    // Load invite based on route param
    this.subs.sink = this.route.params
      .subscribe(
        params => {
          this.invitationCode = params['code'];
          this.subs.sink = this.accountService.getInvite(this.invitationCode)
            .subscribe((invite) => {

              this.store.dispatch(new SetPrivateLabelLogoAction( invite.workspacePrivateLabelLogo));

              this.updateFormValues(invite);
              this.initForm();
            }, (error) => this.handleError(error));
        }, (error) => this.handleError(error));
  }

  private initForm() {

    if (this.requireConfirmPassword) {
      this.inviteDetailsForm = this.formBuilder.group({
        firstName: new UntypedFormControl(null, [Validators.required]),
        lastName: new UntypedFormControl(null, [Validators.required]),
        password: new UntypedFormControl(null, [Validators.required]),
        confirmPassword: new UntypedFormControl()
      });

      const password = this.inviteDetailsForm.controls['password'];
      const confirmPassword = this.inviteDetailsForm.controls['confirmPassword'];

      password.setValidators(Validators.compose([Validators.required,
        CustomValidators.passwordPolicy(password)] ));

      confirmPassword.setValidators(Validators.compose([Validators.required,
        CustomValidators.equal(password)]));

      password.valueChanges.subscribe(() => confirmPassword.updateValueAndValidity());

    } else {
      // Since this user already exists, don't ask for First Name, Last Name, or Confirm Password
      this.inviteDetailsForm = this.formBuilder.group({
        firstName: new UntypedFormControl(),
        lastName: new UntypedFormControl(),
        password: new UntypedFormControl(null, [Validators.required]),
        confirmPassword: new UntypedFormControl()
      });

      const password = this.inviteDetailsForm.controls['password'];
      password.setValidators(Validators.compose([Validators.required,
        CustomValidators.passwordPolicy(password)] ));
    }
  }

  private updateFormValues(invite: Invite) {

    if (invite == null) {
      return;
    }

    this.email = invite.email;
    this.userId = invite.userId;
    this.requireConfirmPassword = (invite.userId === null || invite.userId === undefined);

    if (!this.requireConfirmPassword) {
      this.firstName.setValue(invite.firstName);
      this.lastName.setValue(invite.lastName);
    }
  }

  onJoin() {
    this.isSubmitting = true;
    this.communicationState.setStartState();
    this.subs.sink = this.join()
      .subscribe(() => {
        this.signIn();
        /*
                // Log the user in now that they are registered
                this.signIn()
                  .takeUntil(this.ngOnDestroy$)
                  .subscribe((response) => {
                    this.authService.setData(response.access_token, response.userName, response.userRole);
                    this.inviteDetailsForm.reset();
                    this.isSubmitting = false;
                    this.goToMain();

                  }, (error) => this.handleError(error));
                  */

      }, (error) => this.handleError(error));
  }

  join() {
    this.feedbackMessages = [];

    const payload = {
      firstName: this.inviteDetailsForm.get('firstName').value,
      lastName: this.inviteDetailsForm.get('lastName').value,
      password: this.inviteDetailsForm.get('password').value,
      confirmPassword: this.inviteDetailsForm.get('confirmPassword').value,
      invitationCode: this.invitationCode,
      userId: this.userId
    };

    // If we aren't requiring a confirm password, set it to the user entered
    // password to get past validation. Also include the first & last name for
    // validation
    if (!this.requireConfirmPassword) {
      payload.confirmPassword = this.inviteDetailsForm.get('password').value;
      payload.firstName = this.firstName.value;
      payload.lastName = this.lastName.value;
    }

    return this.accountService.join(payload);
  }

  signIn() {

    const request: SignInRequest = {
      // grant_type: 'password',
      email: this.email,
      password: this.inviteDetailsForm.value.password,
      isPersistent: true,
      rememberBrowser: true
    };

    this.store.dispatch(new LoginAction(request));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  goToMain() {
    this.router.navigate(['useraccount/user/properties']);
  }

  private handleError(errors) {
    this.isSubmitting = false;
    this.feedbackMessages = errors;
    this.communicationState.setFailedState(errors);
    this.showSigninLink = true;
  }
}
